import theme from '../../App/theme';
import { focusRing } from '../../App/controlStates';

/**
 * Every circular control that floats over a slide - the thumbnail actions, the
 * prev/next arrows, the reactions - answers the pointer and the keyboard in the
 * same way, and each of them used to spell that out inline. The copies had
 * already drifted: the arrows had no hover or focus state at all, and the
 * reactions moved their plate by 0.02 alpha, which is nothing.
 *
 * None of them can simply gain opacity on hover, because they sit on slide
 * content of arbitrary colour: a whiter white plate is invisible over a white
 * slide, and a blacker black one is invisible over a black slide. So a surface
 * pairs a plate that changes colour with a ring that does not depend on the
 * plate at all - whichever of the two a given slide defeats, the other reads.
 *
 * A surface is: how the plate looks at rest, how it looks under the pointer,
 * and the ring keyboard focus draws on top of the hovered look.
 */
export const slideControlSx = (surface) => ({
    background: surface.plate,
    boxShadow: surface.shadow,
    '&:hover': {
        background: surface.hoverPlate,
        boxShadow: surface.hoverShadow
    },
    // Keyboard focus is the hover state plus a ring: the pointer's answer is
    // already the right one, it just needs to say which control it is on.
    '&:focus-visible, &.Mui-focusVisible': {
        background: surface.hoverPlate,
        boxShadow: surface.hoverShadow,
        ...focusRing(surface.ring, { halo: surface.ringHalo })
    }
});

/**
 * A translucent white plate under a dark icon: the thumbnail actions, and the
 * reactions with a resting plate of their own. Hover goes light grey rather
 * than more opaque white, which reads as a darkening over a light slide and as
 * a lightening over a dark one.
 *
 * Against the hovered plate, by resting plate and slide:
 *
 *   resting plate          white slide   mid grey   black slide
 *   0.5  (actions)         1.53x         1.17x      2.47x
 *   0.7  (reactions)       1.53x         1.31x      1.28x
 *   0.95 (a given one)     1.53x         1.50x      1.42x
 *
 * MUI's own stock IconButton hover manages 1.09x over any of them. Mid grey is
 * the weak column - a light plate over a light slide starts close to the hover
 * colour whatever it does - so the shadow deepens as well.
 */
export const lightPlate = {
    plate: 'rgba(255,255,255,0.5)',
    shadow: '0px 1px 1px 1px #888888',
    hoverPlate: 'rgba(208,208,208,0.98)',
    hoverShadow: '0px 1px 3px 1px #555555',
    ring: theme.palette.primary.main
};

/**
 * A translucent black plate under a white icon: the prev/next arrows. Hover
 * deepens it rather than lightening it, for the same reason in reverse.
 *
 *   slide       resting    hovered    ratio
 *   white       #a6a6a6    #474747    3.78x
 *   mid grey    #7d7d7d    #363636    2.94x
 *   black       #000000    #000000    1.00x
 *
 * Black over black is the case the plate cannot answer at all, which is why
 * this surface carries a light ring on hover as well as on focus - over a dark
 * slide the ring is the whole of the hover.
 */
export const darkPlate = {
    plate: 'rgba(0,0,0,0.35)',
    shadow: 'none',
    hoverPlate: 'rgba(0,0,0,0.72)',
    hoverShadow: '0 0 0 2px rgba(255,255,255,0.6), 0 2px 8px rgba(0,0,0,0.45)',
    // A navy ring would disappear against a dark slide, which is most of what
    // these sit on, so the ring is white and a dark halo carries it on a light
    // slide instead.
    ring: '#ffffff',
    ringHalo: '0 0 0 4px rgba(0,0,0,0.45)'
};
